import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  Auth,
  Device,
  FacetValue,
  ProductFacet,
  ProductSort,
  ReduxPagination,
  User,
} from '@kion/data-models';
import { DevicesFacade } from '@kion/data-access/data-access-devices';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { filter, map, take } from 'rxjs/operators';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { DropDownOption } from '../../../../../../shared/ui/src/lib/shared/elements/drop-down/drop-down.component';
import { TranslateService } from '@ngx-translate/core';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { FilterChip } from '../../../../../../shared/ui/src/lib/shared/elements/filter-chips/filter-chips.component';
import { UsersFacade } from '@kion/data-access/data-access-users';

@Component({
  selector: 'kion-truck-list',
  templateUrl: './truck-list.component.html',
  styleUrls: ['./truck-list.component.scss'],
})
export class TruckListComponent implements OnInit {
  tableHeadlines = [
    'device',
    'customer',
    'networkPartner',
    'status',
    'lastChange',
  ];
  devices$: Observable<Device[]>;
  pagination$: Observable<ReduxPagination>;
  sorts$: Observable<DropDownOption[]>;
  networkPartners$: Observable<DropDownOption[]>;
  totalResult$: Observable<number>;

  resetValue = false;

  private filterChipAll$: BehaviorSubject<FilterChip> =
    new BehaviorSubject<FilterChip>({
      id: '',
      name: 'all',
      quantity: '',
      active: true,
    });

  private dynamicFilterChips$: Observable<FilterChip[]>;
  latestSortUsed$: BehaviorSubject<DropDownOption> =
    new BehaviorSubject<DropDownOption>(null);

  filterChips$: Observable<FilterChip[]>;
  query$: Observable<string>;

  constructor(
    private devicesFacade: DevicesFacade,
    private observer: CorrelationObserverService,
    private translateService: TranslateService,
    private usersFacade: UsersFacade
  ) {}

  ngOnInit(): void {
    this.devicesFacade.search();
    this.totalResult$ = this.devicesFacade.totalResult$;
    this.devices$ = this.devicesFacade.searchResults$;
    this.pagination$ = this.devicesFacade.pagination$;
    this.query$ = this.devicesFacade.query$;
    setTimeout(() => {
      this.networkPartners$ = this.devicesFacade.facets$.pipe(
        map((facets: ProductFacet[]) => {
          const nwpValues = [];
          const nwpFilter = facets.find(
            (facet) => facet.code === 'networkpartner'
          );
          if (nwpFilter) {
            nwpFilter.values.forEach((value: FacetValue) => {
              nwpValues.push({
                key: value.query?.query?.value,
                value: value.name + ' (' + value.count + ')',
                selected: value.selected || nwpFilter.values.length === 1,
              });
            });
            nwpValues.sort(function (a, b) {
              return a.value.localeCompare(b.value);
            });
            nwpValues.unshift({
              key: 'all',
              value: this.translateService.instant('filter.all'),
              selected:
                nwpValues.filter((option) => option.selected === true)
                  .length === 0,
            });
          }
          return nwpValues;
        })
      );
      combineLatest([
        this.networkPartners$,
        this.dynamicFilterChips$,
      ]).subscribe(([nwp, dfc]) => {
        this.filterChipAll$.next({
          id: '',
          name: 'all',
          quantity: '',
          active: this.isNetworkPartnerAll(nwp, dfc),
        });
      });
    }, 1000);
    setTimeout(() => {
      this.sorts$ = this.devicesFacade.sorts$.pipe(
        map((sorts: ProductSort[]) =>
          sorts.map((sort: ProductSort) => {
            return {
              key: sort.code,
              value: this.translateService.instant('sortBy.' + sort.code),
              selected: sort.selected,
            };
          })
        )
      );
    }, 1000);
    this.dynamicFilterChips$ = this.devicesFacade.facets$.pipe(
      map((facets: ProductFacet[]) => {
        const filterChips = [];
        const statusesFilter = facets.find(
          (facet) => facet.code === 'quoteStatus'
        );
        if (statusesFilter) {
          statusesFilter.values.forEach((value: FacetValue) => {
            filterChips.push({
              id: value.query?.query?.value,
              name: value.name.toLowerCase(),
              quantity: value.count,
              active: value.selected,
            });
          });
        }
        const myRequestsFilter = facets.find(
          (facet) => facet.code === 'quoteEmployeeUid'
        );
        if (myRequestsFilter) {
          this.usersFacade.all$.pipe(take(1)).subscribe((users: User[]) => {
            const user = users[0];
            if (user) {
              const myFilter: FacetValue = myRequestsFilter.values.find(
                (value: FacetValue) => value.code === user.uid
              );
              if (myFilter) {
                filterChips.push({
                  id: decodeURIComponent(myFilter.query?.query?.value),
                  name: 'myRequest',
                  quantity: myFilter.count,
                  active: myFilter.selected,
                });
              }
            }
          });
        }
        return filterChips;
      })
    );
    this.filterChips$ = combineLatest([
      this.totalResult$,
      this.filterChipAll$,
      this.dynamicFilterChips$,
    ]).pipe(
      map(([totalResult, filterChipAll, dynamicFilterChips]) => {
        const filterChips = [
          {
            ...filterChipAll,
            quantity: totalResult,
          },
          ...dynamicFilterChips,
        ];
        return filterChips;
      })
    );
  }

  private isNetworkPartnerAll(
    networkPartners: DropDownOption[],
    filterChips: FilterChip[]
  ): boolean {
    const selectedNetworkPartners = networkPartners.filter(
      (option) => option.selected === true
    );
    if (selectedNetworkPartners[0]?.key === 'all') {
      return filterChips.filter((df) => df.active).length === 0;
    }
    return false;
  }

  perPageChanged(perPage: number): void {
    this.observer.subscribeToAction(
      this.devicesFacade.updatePerPage(perPage),
      () =>
        this.query$
          .pipe(take(1))
          .subscribe((query) => this.devicesFacade.search(query, true))
    );
  }

  pageChanged(page: number): void {
    this.observer.subscribeToAction(this.devicesFacade.updatePage(page), () =>
      this.query$
        .pipe(take(1))
        .subscribe((query) => this.devicesFacade.search(query, true))
    );
  }

  sortChanged(option: DropDownOption): void {
    this.observer.subscribeToAction(
      this.devicesFacade.updateSort(option.key),
      () =>
        this.query$
          .pipe(take(1))
          .subscribe((query) => this.devicesFacade.search(query, true))
    );
  }

  networkPartnerChanged(option: DropDownOption): void {
    if (option.key === 'all') {
      this.devicesFacade.search(null, false, true);
      this.resetValue = true;
      return;
    }
    this.observer.subscribeToAction(
      this.devicesFacade.search(
        decodeURIComponent(option.key.toString()),
        true,
        true
      ),
      () => {
        if (this.latestSortUsed$.value) {
          this.sortChanged(this.latestSortUsed$.value);
        }
      }
    );
  }

  updateFilter(chip: FilterChip) {
    if (chip.id === '') {
      this.filterChipAll$.next({
        ...this.filterChipAll$.value,
        active: chip.id === this.filterChipAll$.value.id,
      });
      this.devicesFacade.search(null, false, true);
      this.resetValue = true;
      return;
    }
    this.filterChipAll$.next({
      ...this.filterChipAll$.value,
      active: chip.id === this.filterChipAll$.value.id,
    });
    this.observer.subscribeToAction(
      this.devicesFacade.search(
        decodeURIComponent(chip.id.toString()),
        true,
        true
      ),
      () => {
        if (chip.id === this.filterChipAll$.value.id) {
          if (this.latestSortUsed$.value) {
            this.sortChanged(this.latestSortUsed$.value);
          }
        }
      }
    );
  }

  search(searchTerm: string) {
    this.sorts$.pipe(take(1)).subscribe((sorts: DropDownOption[]) => {
      const activeSort = sorts.find((sort) => sort.selected);
      if (this.latestSortUsed$.value === null) {
        this.latestSortUsed$.next(activeSort);
      }
    });
    this.filterChipAll$.next({
      ...this.filterChipAll$.value,
      active: false,
    });
    if (searchTerm === '') {
      this.searchBoxCleared();
    }

    this.devicesFacade.search(searchTerm, true, true, 'relevance', true);
  }

  searchBoxResetValue() {
    this.resetValue = false;
  }

  searchBoxCleared() {
    this.filterChipAll$.next({
      ...this.filterChipAll$.value,
      active: true,
    });
    this.observer.subscribeToAction(
      this.devicesFacade.search(null, false, true),
      () => {
        if (this.latestSortUsed$.value) {
          this.sortChanged(this.latestSortUsed$.value);
        }
      }
    );
  }
}
